import { useRef, useEffect } from 'react';

/**
 * Custom hook for scroll-triggered CSS animations using IntersectionObserver.
 *
 * On mount, sets the element to opacity 0 and translateY(30px) with a CSS transition.
 * When the element enters the viewport, animates to opacity 1 and translateY(0),
 * then disconnects the observer (animate once).
 *
 * @param {Object} options
 * @param {number} [options.threshold=0.1] - How much of the element must be visible to trigger.
 * @param {string} [options.rootMargin='0px 0px -50px 0px'] - Observer root margin.
 * @param {number} [options.delay=0] - Transition delay in ms for staggered animations.
 * @returns {React.RefObject} ref to attach to the target element.
 */
export default function useScrollReveal(options = {}) {
  const { threshold = 0.1, rootMargin = '0px 0px -50px 0px', delay = 0 } = options;
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Check prefers-reduced-motion — skip animations entirely
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      el.style.opacity = '1';
      el.style.transform = 'translateY(0)';
      return;
    }

    // Fallback: if IntersectionObserver is not supported, show immediately
    if (!('IntersectionObserver' in window)) {
      el.style.opacity = '1';
      el.style.transform = 'translateY(0)';
      return;
    }

    // Set initial hidden state
    el.style.opacity = '0';
    el.style.transform = 'translateY(30px)';
    el.style.transition = delay
      ? `opacity 0.6s ease ${delay}ms, transform 0.6s ease ${delay}ms`
      : 'opacity 0.6s ease, transform 0.6s ease';

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateY(0)';
            observer.disconnect();
          }
        });
      },
      { threshold, rootMargin }
    );

    observer.observe(el);

    return () => observer.disconnect();
  }, [threshold, rootMargin, delay]);

  return ref;
}
